import * as React from "react";
import { cn } from "@/lib/utils";
import { UseFormRegisterReturn } from "react-hook-form";
import { cva, type VariantProps } from "class-variance-authority";
import { REGEX_PHONE_NUMBER } from "@/constants";

/**
 * Input Component
 *
 * A customizable input field supporting size variants, labels, error display,
 * and react-hook-form integration. Also includes optional type-based logic
 * (like phone input with validation).
 */
const inputVariants = cva(
  cn(
    "placeholder:text-gray-normal text-gray-stronger flex min-w-0 border border-[var(--stroke-gray-light)] bg-transparent outline-none",
    "hover:border-primary-800",
    "focus:border-primary-800 active:border-primary-800 active:shadow-input-active",
    "disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-input-disable disabled:border-gray-200 disabled:text-gray-light",
    "file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground ",
  ),
  {
    variants: {
      size: {
        lg: "h-px-44 rounded-input-lg px-[14px] py-[10px]",
        md: "h-px-40 rounded-input-md px-[12px] py-[8px]",
        sm: "h-px-32 rounded-input-sm px-[10px] py-[6px]",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

const inputType = {
  phone: {
    type: "tel",
    pattern: "[0-9]*",
    onKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!REGEX_PHONE_NUMBER.test(e.key)) {
        e.preventDefault();
      }
    },
  },
};

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {
  label?: string;
  error?: string;
  register?: UseFormRegisterReturn;
  labelClassName?: string;
  inputType?: keyof typeof inputType;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      error,
      register,
      size,
      labelClassName,
      inputType: inputTypeKey,
      ...props
    },
    ref,
  ) => {
    const { required } = props;
    // Determine if the input has a special type configuration (like phone)
    const inputTypeProps = inputTypeKey ? inputType[inputTypeKey] : {};
    return (
      <div className="flex w-[inherit] flex-col gap-1">
        {label && (
          <label
            className={cn("text-sm font-medium text-gray-800", labelClassName)}
          >
            {label} {required && <span className="text-red-700">*</span>}
          </label>
        )}
        <input
          className={cn(
            inputVariants({ size, className }),
            error &&
              "!border-red-700 focus:!border-red-700 focus:!ring-red-700",
            className,
          )}
          ref={ref}
          {...inputTypeProps}
          {...register}
          {...props}
        />
        {error && <p className="text-xs text-red-700">{error}</p>}
      </div>
    );
  },
);
Input.displayName = "Input";
export { Input };
